// Shared audio for the SCRUM Nebula. Main bundle on purpose and tiny: the
// AudioContext must be created inside a user gesture (the Konami keydown), so
// the launcher owns it and the lazy game chunk reuses it. One per page, never
// closed (browsers cap live contexts).
const KEY = "massif_scrum_nebula_muted";
let ctx: AudioContext | null = null;
const listeners = new Set<() => void>();

function setAudioSession(type: "auto" | "playback") {
  try {
    const session = (
      navigator as Navigator & { audioSession?: { type: string } }
    ).audioSession;
    if (session) session.type = type;
  } catch {
    // Optional Safari API: failure must not prevent normal Web Audio playback.
  }
}

export function loadMuted(
  read: () => string | null = () => localStorage.getItem(KEY)
) {
  try {
    return read() === "1";
  } catch {
    return false; // Storage can be unavailable in private mode or on the server.
  }
}
let muted = loadMuted();

export const isMuted = () => muted;

export function setMuted(next: boolean) {
  muted = next;
  if (next) setAudioSession("auto");
  // A sound control can be the first gesture after the ?konami shortcut.
  if (!next && ctx) unlockAudio();
  try {
    localStorage.setItem(KEY, next ? "1" : "0");
  } catch {
    // Storage can be unavailable; keep the choice for this session.
  }
  listeners.forEach((l) => l());
}

export function subscribeMuted(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Creates the context on first call, resumes it after; call from a gesture.
export function unlockAudio() {
  if (typeof AudioContext === "undefined") return null;
  try {
    ctx ??= new AudioContext();
    // A replacement title can arrive while the previous suspend is still pending.
    if (!document.hidden) {
      // iOS's default ambient session obeys Silent Mode even after resume().
      if (!muted) setAudioSession("playback");
      void ctx.resume().catch(() => {});
    }
    return ctx;
  } catch {
    return null;
  }
}

export const audioContext = () => ctx;

export function suspendAudio(context: AudioContext | null = ctx) {
  setAudioSession("auto");
  void context?.suspend().catch(() => {});
}

// The boot overlay's recorded disk seek, loaded only after opening the game.
export function bootChirp(c: AudioContext) {
  let alive = true;
  let stopRecording: (() => void) | undefined;
  const stop = () => {
    alive = false;
    stopRecording?.();
    off();
  };
  const off = subscribeMuted(() => {
    if (muted) stop();
  });
  if (!muted && !document.hidden) {
    const resume = c.state === "running" ? Promise.resolve() : c.resume();
    void Promise.all([resume, import("./game/recordings")])
      .then(async ([, recordings]) => {
        if (!alive) return;
        const buffer = await recordings.loadRecording(c, recordings.BOOT_URL);
        if (!alive || muted || document.hidden || c.state !== "running") return;
        stopRecording = recordings.playRecording(c, c.destination, buffer, 0.1);
      })
      .catch(() => {});
  }
  return stop;
}
